// src/lib/redirects/trailingJunk.ts

/**
 * Edge-safe normalizer that strips crawler-appended trailing junk from a URL
 * pathname's final segment.
 *
 * Why this exists
 * ───────────────
 * Bots (notably Amazonbot) routinely mis-parse Markdown/HTML links and request
 * the real URL with stray punctuation glued onto the end — the closing paren of
 * a Markdown `[label](url)`, a trailing `].,:;`, or a percent-encoded tail. In
 * production this showed up as a steady stream of requests like:
 *
 *   /resources/tools/calculators/ad-spend-roi-calculator)        ← real page + ")"
 *   /resources/glossary/general/retention-marketing)             ← real page + ")"
 *   /resources/tools/calculators/target-roas-calculator.md))%3A  ← real page + ".md)):"
 *
 * Most of these are a VALID slug with one or two junk characters appended, so
 * the correct response is not a bare 404 — it's to normalize the junk off and
 * redirect to the real URL (recovering the page, its traffic, and its link
 * equity), exactly like the trailing-slash and `/en` normalizers already in
 * `middleware.ts`. Slugs that are still unknown after cleaning fall through to a
 * clean `notFound()` on the route as before.
 *
 * Contract
 * ────────
 * - MUST stay free of Node.js APIs / non-deterministic globals (runs in the Edge
 *   middleware). Pure string transform only.
 * - Only ever removes a run of junk at the VERY END of the pathname. Internal
 *   junk (`/met)rics/churn-rate`) is left alone — that's a genuinely different
 *   URL and should 404 normally, not be silently rewritten.
 * - Idempotent: the cleaned pathname contains no trailing junk, so re-running it
 *   is a no-op (no redirect loop).
 *
 * What counts as "junk" — by ALLOWLIST, not denylist. A route URL path validly
 * ends in a slug character (`[a-zA-Z0-9-]`; the only dotted form is a `*.md`
 * alternate, which ends in `d`). So the scan stops at the first valid trailing
 * character and treats EVERYTHING else at the edge as junk — brackets, quotes,
 * `:;,.!`, whitespace, any punctuation, in literal OR percent-encoded form. A
 * denylist of junk characters can never be complete (each new crawler-appended
 * char/encoding is a fresh gap); the allowlist closes the whole class at once.
 * `.md` survives because its `.` is internal — `cpm.md` stops at `d`, while
 * `cpm.md).` strips `).` back to `cpm.md`.
 *
 * Percent-encoding is DECODED before deciding, and only ASCII bytes are judged:
 * `%29`→`)` and `%21`→`!` are junk; `%72`→`r` is a valid char so it STOPS the
 * scan (an over-encoded but legitimate `…/calculato%72` is the real
 * `…/calculator` — Next decodes it, so we must not strip it); `%2F`→`/` is a
 * slash unit that, UNLIKE a literal trailing slash, is normalized here because
 * STEP 2 can't see it (see below); a byte ≥ 0x80 is one octet of a multi-byte UTF-8 char,
 * so we STOP and leave the whole char (stripping one octet, e.g. of an NBSP
 * `%C2%A0`, would leave a dangling lead byte). (#402 `r3481598063`, `r3481915322`)
 *
 * SITE-WIDE INVARIANT: this runs on EVERY matched page path, not just
 * `/resources/*`, so "no route URL ends outside `[a-zA-Z0-9_~-]`" is a site-wide
 * assumption (pinned by the registry invariant test in
 * `__tests__/trailingJunk.test.ts`). A future i18n route ending in a non-ASCII
 * character would need to revisit the allowlist.
 *
 * Implementation is a single BACKWARD SCAN (no regex), so it is O(n) with no
 * backtracking — structurally immune to super-linear ReDoS. A length guard still
 * bails implausibly long paths cheaply.
 */
/**
 * The ALLOWLIST: characters that can legitimately END a route URL. Every route
 * slug is `[a-z0-9-]` and the only dotted form is a `*.md` alternate (which ends
 * in `d`). So a URL path validly ends in a slug character — letters, digits, or a
 * hyphen — and ANYTHING else at the trailing edge is crawler-appended junk.
 *
 * This is an allowlist on PURPOSE: a denylist of "junk characters" can never be
 * complete (every new punctuation/encoding a crawler appends — `)`, `!`, `:`,
 * `%2F`, … — would be a new gap). Stopping only at a valid trailing character
 * closes the whole class at once.
 *
 * The set is RFC 3986 "unreserved" (`A-Z a-z 0-9 - _ ~`) MINUS `.`: our slugs are
 * `[a-z0-9-]`, but `_`/`~` are valid URL-path characters that a route could
 * legitimately end in, so — to keep the allowlist from being MORE aggressive than
 * the old denylist (which never stripped them) — they STOP the scan too. `.` is
 * deliberately excluded so a trailing `.` is junk (`cpm.md).` → `cpm.md`); the
 * `.` inside `.md` is internal, never the trailing char. (The `-` is placed last
 * in the class so it's a literal, not a range.)
 */
function isValidTrailingChar(ch: string): boolean {
  return /^[a-zA-Z0-9_~-]$/.test(ch)
}

function isHexDigit(ch: string): boolean {
  return /^[0-9A-Fa-f]$/.test(ch)
}

/**
 * Above this length a pathname is not a real slug URL (the longest legitimate
 * route path is well under this), so we bail early — purely to skip pointless
 * work on absurd inputs (the scan itself is already O(n) with no backtracking).
 */
const MAX_NORMALIZABLE_PATH_LENGTH = 1024

/**
 * Returns the cleaned pathname when `pathname` ends in crawler-appended junk and
 * a real final segment survives the strip, otherwise `null` (nothing to do).
 *
 * Idempotent for ALL inputs (including junk-then-slash like `/x)/`): the single
 * backward scan consumes trailing junk characters, trailing slashes a junk run
 * exposes, and trailing `%XX` triplets that DECODE to junk — in any order —
 * until it reaches a real character, so it does not rely on the trailing-slash
 * normalizer having run first (the caller can safely run this BEFORE it). The
 * returned value ends in a non-junk character, so re-running it yields `null`.
 *
 * A pathname whose only trailing removable run is LITERAL slashes (`/foo/`, no
 * junk) returns `null` — that's the trailing-slash normalizer's (STEP 2) job. But
 * a trailing run containing an ENCODED slash (`/foo%2F`, `/foo/%2F`) IS cleaned
 * here: STEP 2 only matches a literal `/`, so an encoded-slash tail would
 * otherwise fall through to a 404. (Next keeps `%2F` encoded — it is not decoded
 * to a separator — so `/foo%2F` is a distinct, non-existent path.)
 *
 * @param pathname A URL pathname (no query/hash), e.g. `request.nextUrl.pathname`.
 */
export function stripTrailingPathJunk(pathname: string): string | null {
  // Never touch the root, and a junk-only request like `/)` has no real segment
  // to recover — let it 404 normally rather than redirect toward `/`.
  if (pathname === '/' || pathname.length === 0) return null

  // Skip implausibly long paths cheaply. No real slug path approaches this.
  if (pathname.length > MAX_NORMALIZABLE_PATH_LENGTH) return null

  // Walk backward over the trailing run, classifying each unit (a literal char
  // or a `%XX` triplet) by the ALLOWLIST:
  //   - a VALID trailing char (`[a-zA-Z0-9_~-]`, literal or encoded) → STOP,
  //   - a SLASH (literal `/` or `%2F`) → consume as a slash unit (NOT junk),
  //   - a `%XX` whose byte is ≥ 0x80 → STOP (one octet of a multi-byte UTF-8 char;
  //     stripping a single octet would leave a dangling lead byte — leave it whole
  //     for Next to decode, e.g. an NBSP `%C2%A0`),
  //   - anything ELSE → strip as JUNK.
  // This covers every punctuation/encoding a crawler can append (`)`, `!`, `:`,
  // `%2F`, `%21`, …) and every future one, because we stop only at a valid char.
  //
  // `strippedJunk` tracks whether we removed any ACTUAL junk (not just slashes).
  // `sawEncodedSlash` tracks whether the consumed run included an ENCODED slash
  // (`%2F`). The two together decide who owns a pure trailing-slash run:
  //   - pure LITERAL trailing slash (`/foo/`)  → return null; STEP 2 (the
  //     trailing-slash normalizer) owns it (it matches a literal `/`).
  //   - a trailing run containing an ENCODED slash (`/foo%2F`, `/foo/%2F`) →
  //     normalize it HERE, because STEP 2 only matches a LITERAL `/` and would
  //     let an encoded-slash tail fall through to a 404 (#402 review). Next
  //     keeps `%2F` encoded (it is NOT decoded to a path separator), so
  //     `/foo%2F` is a distinct, non-existent path that should 308 to `/foo`.
  // This lets the caller run this BEFORE the slash redirect to catch
  // junk-then-slash (`/foo)/`, `/foo)%2F`) AND pure encoded-slash tails in one
  // pass, while leaving plain literal trailing-slash handling to STEP 2.
  // `sawDotInSegment` tracks whether the run of junk SINCE the last slash
  // boundary contained a dot (`.` or `%2E`). If we then CONSUME a slash — i.e.
  // collapse that whole segment to its parent — and the segment was dot-bearing
  // junk, it was a `.`/`..` path-traversal token, NOT a real slug with a junk
  // suffix. Collapsing it would 308 (and day-cache) a malformed traversal URL to
  // an UNRELATED valid parent page; reject instead and let it 404. A dot that is
  // a junk SUFFIX on a real slug (`cpm.)` → `cpm`) never crosses a slash, so it
  // is unaffected. (#402 `r3482950248`)
  let end = pathname.length
  let strippedJunk = false
  let sawEncodedSlash = false
  let sawDotInSegment = false
  for (;;) {
    if (end === 0) break

    if (
      end >= 3 &&
      pathname[end - 3] === '%' &&
      isHexDigit(pathname[end - 2] ?? '') &&
      isHexDigit(pathname[end - 1] ?? '')
    ) {
      const byte = parseInt(pathname.slice(end - 2, end), 16)
      if (byte === 0x2f) {
        // Encoded slash → slash unit (not junk). Collapsing a dot-only segment
        // here is a path-traversal recovery we must NOT make → reject.
        if (sawDotInSegment) return null
        end -= 3
        sawEncodedSlash = true
        sawDotInSegment = false
        continue
      }
      if (byte >= 0x80) break // multi-byte octet → leave the whole char
      if (isValidTrailingChar(String.fromCharCode(byte))) break // e.g. `%72`→`r`
      end -= 3
      strippedJunk = true
      if (byte === 0x2e) sawDotInSegment = true // `%2E` → `.`
      continue
    }

    const prev = pathname[end - 1] ?? ''
    if (prev === '/') {
      // Crossing a segment boundary: if the segment we just stripped was
      // dot-bearing junk (`..`, `.`), this is a traversal collapse → reject.
      if (sawDotInSegment) return null
      end -= 1
      sawDotInSegment = false
      continue
    }
    if (isValidTrailingChar(prev)) break
    end -= 1
    strippedJunk = true
    if (prev === '.') sawDotInSegment = true
  }

  // Nothing removed, or ONLY literal slashes were removed (no junk, no encoded
  // slash) → not this function's concern; let the trailing-slash normalizer
  // (STEP 2) handle the literal-slash case.
  if (!strippedJunk && !sawEncodedSlash) return null

  const cleaned = pathname.slice(0, end)

  // No change, or nothing meaningful left → not our case.
  if (cleaned === pathname || cleaned === '' || cleaned === '/') return null

  return cleaned
}
